import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format number in Indian currency format
export function formatCurrency(amount, decimals) {
  if (amount === null || amount === undefined) return "₹0";
  let num = Number(amount);
  if (isNaN(num)) return "₹0";
  // Avoid displaying "-₹0"
  if (Object.is(num, -0) || (num > -0.005 && num < 0)) num = 0;

  const fracDigits = decimals !== undefined ? decimals : (Math.abs(num % 1) > 0.0001 ? 2 : 0);
  const formatted = num.toLocaleString('en-IN', {
    minimumFractionDigits: fracDigits,
    maximumFractionDigits: fracDigits,
    style: 'currency',
    currency: 'INR'
  });
  return formatted;
}

// Format date in DD/MM/YYYY
export function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// Format date for display
export function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

// Get month name
export function getMonthName(month) {
  const months = ["", "January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];
  return months[month] || "";
}

// Get relative time
export function getRelativeTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
}

// Status badge colors
export function getStatusColor(status) {
  const colors = {
    generated: "bg-gray-100 text-gray-800",
    sent: "bg-blue-100 text-blue-800",
    submitted: "bg-orange-100 text-orange-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    draft: "bg-gray-100 text-gray-800",
    finalized: "bg-blue-100 text-blue-800",
    paid: "bg-green-100 text-green-800",
    pending: "bg-orange-100 text-orange-800",
    partial: "bg-yellow-100 text-yellow-800",
    received: "bg-green-100 text-green-800",
    overdue: "bg-red-100 text-red-800",
    cancelled: "bg-red-100 text-red-800",
    active: "bg-green-100 text-green-800",
    inactive: "bg-gray-100 text-gray-800"
  };
  return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
}

// Validation helpers
export function isValidGST(gst) {
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstRegex.test(gst);
}

export function isValidPAN(pan) {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
}

export function isValidPhone(phone) {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone?.replace(/\D/g, ''));
}

export function cleanInvoiceData(inv) {
  if (!inv) return inv;
  let items = (inv.line_items || []).map(it => {
    let desc = (it.description || '').trim();
    if (/^\d+\s*[\*xX×]\s*\d+/i.test(desc) || /^[\d\s\*xX×\.\,\-\+]+$/.test(desc)) {
      desc = 'Monthly Service and Operation Charges';
    }
    return { ...it, description: desc };
  });

  const hasServiceCharge = items.some(it => (it.description || '').toLowerCase().includes('service charge'));
  const marginAmt = Number(inv.margin_amount || 0);
  const marginPct = Number(inv.margin_percent || 0);

  if (!hasServiceCharge && marginAmt > 0) {
    const visits = items[0]?.quantity || 1;
    const rate = items[0]?.quantity ? Math.round((marginAmt / items[0].quantity) * 100) / 100 : marginAmt;
    items = [
      ...items,
      {
        id: 'item-sc',
        description: `Service Charges ${marginPct ? `${marginPct}%` : ''}`.trim(),
        quantity: visits,
        rate: rate,
        amount: marginAmt,
        is_auto: true
      }
    ];
  }

  return { ...inv, line_items: items };
}

